import {graphql, useFragment} from 'react-relay'

import type {
  IssueEventConfidenceLevel,
  IssueTypeColor,
  PendingIssueSuggestionState,
  useRecentAgentActivity_issue$data,
  useRecentAgentActivity_issue$key,
} from './__generated__/useRecentAgentActivity_issue.graphql'

export type AgentActionType = 'label_added' | 'type_set' | 'field_set'

export type PendingSuggestionState = PendingIssueSuggestionState

export type SuggestionConfidence = 'HIGH' | 'MEDIUM' | 'LOW'

export type PendingSuggestion = {
  type: AgentActionType
  value: string | null
  color: string | null
  state: PendingSuggestionState
  confidence: SuggestionConfidence | null
  intentId: string | null
  actorId: string
  actorTypename: string
  actorLogin: string
  actorAvatarUrl: string
  actorDisplayName: string | null
  rationale: string | null
  // Used as the label shown on the row (e.g. "Type", "Label", or a field name)
  displayFieldName: string
  // Type suggestions only — used to wire up apply/reject mutations.
  issueTypeId: string | null
  issueTypeName: string | null
  issueTypeColor: IssueTypeColor | null
  // Label suggestions only.
  labelId: string | null
  // Field suggestions only.
  issueFieldId: string | null
}

export type LabelInfo = {
  name: string
  color: string | null
  rationale?: string
}

export type AgentAction = {
  type: AgentActionType
  fieldName: string
  value: string | null
  createdAt: string
  color: string | null
  labels?: LabelInfo[]
  rationale?: string
}

export type AgentSuggestionsGroup = {
  agentName: string
  agentSlug: string
  actorLogin: string
  actorAvatarUrl: string
  suggestions: PendingSuggestion[]
}

export type AgentActivityGroup = {
  agentName: string
  agentSlug: string
  actorLogin: string
  actorAvatarUrl: string
  actions: AgentAction[]
}

export type ProcessedAgentActivity = {
  hasRecentActivity: boolean
  groups: AgentActivityGroup[]
  totalActionCount: number
}

export type AgentSuggestionsResult = {
  hasPendingSuggestions: boolean
  groups: AgentSuggestionsGroup[]
  totalSuggestionCount: number
}

export type RecentAgentActivityResult = ProcessedAgentActivity & {
  issueId: string
  viewerCanUpdateMetadata: boolean
  repoNameWithOwner: string
  pendingSuggestions: AgentSuggestionsResult
}

type LabelAction = AgentAction & {labels: LabelInfo[]}

export const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

export function computeRecentActivitySince(): string {
  const hourMs = 60 * 60 * 1000
  const rounded = Math.floor((Date.now() - SEVEN_DAYS_MS) / hourMs) * hourMs
  return new Date(rounded).toISOString()
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const recentAgentActivityFragment = graphql`
  fragment useRecentAgentActivity_issue on Issue @argumentDefinitions(since: {type: "DateTime"}) {
    id
    viewerCanUpdateMetadata
    repository {
      nameWithOwner
    }
    pendingSuggestions {
      __typename
      ... on PendingTypeSuggestion {
        typeState: state
        intent {
          intentId
        }
        actor {
          __typename
          login
          avatarUrl
          id
          ... on Bot {
            displayName
          }
        }
        confidence
        rationale
        issueType {
          id
          name
          color
        }
      }
      ... on PendingLabelSuggestion {
        labelState: state
        intent {
          intentId
        }
        actor {
          __typename
          login
          avatarUrl
          id
          ... on Bot {
            displayName
          }
        }
        confidence
        rationale
        label {
          id
          name
          color
        }
      }
      ... on PendingFieldSuggestion {
        fieldState: state
        intent {
          intentId
        }
        actor {
          __typename
          login
          avatarUrl
          id
          ... on Bot {
            displayName
          }
        }
        rationale
        suggestedValue
        confidence
        issueField {
          ... on Node {
            id
          }
          ... on IssueFieldCommon {
            name
          }
          ... on IssueFieldSingleSelect {
            options {
              name
              color
            }
          }
        }
      }
    }
    recentTriageEvents: timelineItems(
      last: 25
      since: $since
      visibleEventsOnly: true
      itemTypes: [
        LABELED_EVENT
        UNLABELED_EVENT
        ISSUE_TYPE_ADDED_EVENT
        ISSUE_TYPE_CHANGED_EVENT
        ISSUE_TYPE_REMOVED_EVENT
        ISSUE_FIELD_CHANGED_EVENT
        ISSUE_FIELD_ADDED_EVENT
        ISSUE_FIELD_REMOVED_EVENT
      ]
    ) {
      nodes {
        __typename
        ... on LabeledEvent {
          id
          createdAt
          label {
            name
            color
          }
          intent {
            rationale
            confidence
            intentId
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on UnlabeledEvent {
          id
          createdAt
          label {
            name
            color
          }
          intent {
            rationale
            confidence
            intentId
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueTypeAddedEvent {
          id
          createdAt
          issueType {
            name
            color
          }
          intent {
            rationale
            confidence
            intentId
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueTypeChangedEvent {
          id
          createdAt
          issueType {
            name
            color
          }
          intent {
            rationale
            confidence
            intentId
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueTypeRemovedEvent {
          id
          createdAt
          issueType {
            name
            color
          }
          intent {
            rationale
            confidence
            intentId
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueFieldChangedEvent {
          id
          createdAt
          issueField {
            ... on IssueFieldCommon {
              name
            }
          }
          newValue
          newColor
          intent {
            rationale
            confidence
            intentId
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueFieldAddedEvent {
          id
          createdAt
          issueField {
            ... on IssueFieldCommon {
              name
            }
          }
          value
          color
          intent {
            rationale
            confidence
            intentId
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueFieldRemovedEvent {
          id
          createdAt
          issueField {
            ... on IssueFieldCommon {
              name
            }
          }
          intent {
            rationale
            confidence
            intentId
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
      }
    }
  }
`

type TimelineNode = NonNullable<NonNullable<useRecentAgentActivity_issue$data['recentTriageEvents']>['nodes']>[number]

type ActorInfo = {name: string; slug: string; login: string; avatarUrl: string}

function getActorInfo(node: NonNullable<TimelineNode>): ActorInfo | null {
  if (!('actor' in node) || !node.actor) return null
  const displayName = node.actor.__typename === 'Bot' ? node.actor.displayName : undefined
  return {
    name: displayName ?? node.actor.login,
    slug: node.actor.login,
    login: node.actor.login,
    avatarUrl: node.actor.avatarUrl,
  }
}

function toAgentAction(node: NonNullable<TimelineNode>): AgentAction | null {
  const intent = 'intent' in node ? node.intent : undefined
  const rationale = intent?.rationale ?? undefined
  switch (node.__typename) {
    case 'LabeledEvent':
      return {
        type: 'label_added',
        fieldName: 'Label',
        value: node.label?.name ?? null,
        createdAt: node.createdAt,
        color: node.label?.color ?? null,
        rationale,
      }
    case 'IssueTypeAddedEvent':
    case 'IssueTypeChangedEvent':
      return {
        type: 'type_set',
        fieldName: 'Type',
        value: node.issueType?.name ?? null,
        createdAt: node.createdAt,
        color: node.issueType?.color?.toLowerCase() ?? null,
        rationale,
      }
    case 'IssueFieldChangedEvent':
      return {
        type: 'field_set',
        fieldName: node.issueField?.name ?? 'Field',
        value: node.newValue ?? null,
        createdAt: node.createdAt,
        color: node.newColor ?? null,
        rationale,
      }
    case 'IssueFieldAddedEvent':
      return {
        type: 'field_set',
        fieldName: node.issueField?.name ?? 'Field',
        value: node.value ?? null,
        createdAt: node.createdAt,
        color: node.color ?? null,
        rationale,
      }
    default:
      return null
  }
}

function normalizeConfidence(value: IssueEventConfidenceLevel | null | undefined): SuggestionConfidence | null {
  if (value === 'HIGH' || value === 'MEDIUM' || value === 'LOW') return value
  return null
}

export function processPendingSuggestions(
  suggestions: useRecentAgentActivity_issue$data['pendingSuggestions'],
): AgentSuggestionsResult {
  const groupsBySlug = new Map<string, AgentSuggestionsGroup>()
  let totalSuggestionCount = 0

  if (!suggestions) {
    return {
      hasPendingSuggestions: false,
      groups: [],
      totalSuggestionCount: 0,
    }
  }

  for (const suggestion of suggestions) {
    if (
      !suggestion ||
      (suggestion.__typename !== 'PendingTypeSuggestion' &&
        suggestion.__typename !== 'PendingLabelSuggestion' &&
        suggestion.__typename !== 'PendingFieldSuggestion')
    ) {
      continue
    }
    if (!suggestion.actor) continue

    const actor = suggestion.actor
    const actorDisplayName = actor.__typename === 'Bot' ? (actor.displayName ?? null) : null

    let group = groupsBySlug.get(actor.login)
    if (!group) {
      group = {
        agentName: actor.login,
        agentSlug: actor.login,
        actorLogin: actor.login,
        actorAvatarUrl: actor.avatarUrl,
        suggestions: [],
      }
      groupsBySlug.set(actor.login, group)
    }

    const baseSuggestion = {
      actorId: actor.id,
      actorTypename: actor.__typename,
      actorLogin: actor.login,
      actorAvatarUrl: actor.avatarUrl,
      actorDisplayName,
      rationale: suggestion.rationale ?? null,
      confidence: normalizeConfidence(suggestion.confidence),
      intentId: suggestion.intent?.intentId ?? null,
    }

    if (suggestion.__typename === 'PendingTypeSuggestion' && suggestion.issueType) {
      const issueType = suggestion.issueType
      group.suggestions.push({
        ...baseSuggestion,
        type: 'type_set',
        state: suggestion.typeState ?? 'PENDING',
        displayFieldName: 'Type',
        value: issueType.name,
        color: issueType.color ?? null,
        issueTypeId: issueType.id,
        issueTypeName: issueType.name,
        issueTypeColor: issueType.color ?? null,
        labelId: null,
        issueFieldId: null,
      })
      totalSuggestionCount++
    } else if (suggestion.__typename === 'PendingLabelSuggestion' && suggestion.label) {
      const label = suggestion.label
      group.suggestions.push({
        ...baseSuggestion,
        type: 'label_added',
        state: suggestion.labelState ?? 'PENDING',
        displayFieldName: 'Label',
        value: label.name,
        color: label.color ?? null,
        issueTypeId: null,
        issueTypeName: null,
        issueTypeColor: null,
        labelId: label.id,
        issueFieldId: null,
      })
      totalSuggestionCount++
    } else if (suggestion.__typename === 'PendingFieldSuggestion' && suggestion.issueField) {
      const issueField = suggestion.issueField
      const matchingOption =
        suggestion.suggestedValue && issueField.options
          ? issueField.options.find(o => o.name === suggestion.suggestedValue)
          : undefined
      group.suggestions.push({
        ...baseSuggestion,
        type: 'field_set',
        state: suggestion.fieldState ?? 'PENDING',
        displayFieldName: issueField.name ?? 'Field',
        value: suggestion.suggestedValue ?? null,
        color: matchingOption?.color ?? null,
        issueTypeId: null,
        issueTypeName: null,
        issueTypeColor: null,
        labelId: null,
        issueFieldId: issueField.id ?? null,
      })
      totalSuggestionCount++
    }
  }

  const groups = Array.from(groupsBySlug.values())
  groups.sort((a, b) => a.agentSlug.localeCompare(b.agentSlug))

  return {
    hasPendingSuggestions: totalSuggestionCount > 0,
    groups,
    totalSuggestionCount,
  }
}

export function processAgentActivity(nodes: ReadonlyArray<TimelineNode | null>): ProcessedAgentActivity {
  const groupsBySlug = new Map<string, AgentActivityGroup>()
  let totalActionCount = 0

  const labelActionsBySlug = new Map<string, LabelAction>()

  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i]
    if (!node || !('createdAt' in node)) continue

    // Only include events that have an attached rationale (via intent)
    const intent = 'intent' in node ? node.intent : undefined
    if (!intent?.rationale) continue

    const actor = getActorInfo(node)
    if (!actor) continue

    const action = toAgentAction(node)
    if (!action) continue

    let group = groupsBySlug.get(actor.slug)
    if (!group) {
      group = {
        agentName: actor.name,
        agentSlug: actor.slug,
        actorLogin: actor.login,
        actorAvatarUrl: actor.avatarUrl,
        actions: [],
      }
      groupsBySlug.set(actor.slug, group)
    }

    if (action.type === 'label_added') {
      const labelInfo: LabelInfo = {name: action.value ?? '', color: action.color, rationale: action.rationale}
      const existing = labelActionsBySlug.get(actor.slug)
      if (existing) {
        existing.labels.push(labelInfo)
        if (action.createdAt > existing.createdAt) {
          existing.createdAt = action.createdAt
        }
      } else {
        const labelAction: LabelAction = {
          ...action,
          fieldName: 'Labels',
          labels: [labelInfo],
        }
        labelActionsBySlug.set(actor.slug, labelAction)
        group.actions.unshift(labelAction)
      }
      totalActionCount++
    } else {
      group.actions.unshift(action)
      totalActionCount++
    }
  }

  const groups = Array.from(groupsBySlug.values())
  groups.sort((a, b) => a.agentSlug.localeCompare(b.agentSlug))
  for (const group of groups) {
    group.actions.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  }

  return {
    hasRecentActivity: totalActionCount > 0,
    groups,
    totalActionCount,
  }
}

export function useRecentAgentActivity(issueRef: useRecentAgentActivity_issue$key): RecentAgentActivityResult {
  const data = useFragment(recentAgentActivityFragment, issueRef)
  const nodes = data.recentTriageEvents?.nodes ?? []
  const result = processAgentActivity(nodes)
  const pendingSuggestions = processPendingSuggestions(data.pendingSuggestions ?? [])
  return {
    ...result,
    issueId: data.id,
    viewerCanUpdateMetadata: data.viewerCanUpdateMetadata ?? false,
    repoNameWithOwner: data.repository.nameWithOwner,
    pendingSuggestions,
  }
}
